import { ErrorSnackbar } from '@application/components';
import { FeedbackContext, UserContext } from '@application/contexts';
import { downloadRemoteFile } from '@application/utils/file-utils';
import { extractErrorCodes } from '@application/utils/urql-utils';
import { useGenerateSingleAddressSearchMutation, useSiteNetworksQuery } from '@domain/index';
import { ReportAggregation } from '@domain/graphql.types';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterCategory, toSearchFiltersInput } from '../filters/searchFilters';
import useSiteLookupFilters from '../filters/useSiteLookupFilters';
import { getAvailableSiteLookupDataViews } from '../siteLookupDataView';
import useDataViewPreference from './useDataViewPreference';
import useSiteSearch from './useSiteSearch';

const useSiteLookup = () => {
  const { t } = useTranslation();

  const { refreshUser, tenant, user } = useContext(UserContext);
  const { setFeedback } = useContext(FeedbackContext);

  const { filters, updateFilter } = useSiteLookupFilters();
  const hasHistoricalAccess = !!tenant?.permissions.apiPermissions?.historicalViewAccess;
  const hasRealTimeAccess = !!tenant?.permissions.apiPermissions?.realTimeAccess;
  const maxHistoricalMonths = tenant?.permissions.maxHistoricalMonths ?? 3;

  const { data, error, fetching, generateReport } = useGenerateSingleAddressSearchMutation();
  const availableDataViews = useMemo(
    () => getAvailableSiteLookupDataViews({ hasHistoricalAccess, hasRealTimeAccess }),
    [hasHistoricalAccess, hasRealTimeAccess],
  );
  const { dataView: resolvedDataView, setDataView } = useDataViewPreference({ availableDataViews, userId: user?.id });

  const {
    actions: { applyFilters, retry: retrySearch },
    state: { appliedFilters, failedRequest, pendingRequest, result },
  } = useSiteSearch({ address: filters.address, radius: filters.radius, dataView: resolvedDataView });

  const networks = useSiteNetworksQuery();

  const [openFilter, setOpenFilter] = useState<FilterCategory | null>(null);

  // A new address or radius discards the draft: the popover closes and the search runs with the applied filters.
  // biome-ignore lint/correctness/useExhaustiveDependencies: react to the location only
  useEffect(() => {
    setOpenFilter(null);
  }, [filters.address, filters.radius]);

  // The report always matches the displayed results: same location, radius and applied filters.
  const downloadReport = useCallback(
    (aggregation: ReportAggregation, months: number) => {
      if (!result) {
        return;
      }

      const { address, radius, filters: searchFilters } = result.request;

      generateReport({
        input: {
          aggregation,
          coordinates: address.coordinates,
          fullAddress: address.fullAddress,
          months,
          range: radius,
          stateCode: address.regionCode || '',
          filters: toSearchFiltersInput(searchFilters),
        },
      });
    },
    [result, generateReport],
  );

  useEffect(() => {
    if (!data?.generateSingleAddressSearchReport.link) {
      return;
    }

    downloadRemoteFile(data.generateSingleAddressSearchReport.link);
    refreshUser();
  }, [data, refreshUser]);

  useEffect(() => {
    const errors = extractErrorCodes(error);

    if (!errors.length) {
      return;
    }

    setFeedback(
      <ErrorSnackbar
        message={t('siteLookup.error.downloadReport.message')}
        onClose={() => setFeedback(null)}
        title={t('siteLookup.error.downloadReport.title')}
      />,
    );
  }, [error, setFeedback, t]);

  return {
    actions: {
      applyFilters,
      downloadReport,
      retrySearch,
      setDataView,
      setOpenFilter,
      updateFilter,
    },
    state: {
      appliedFilters,
      dataView: resolvedDataView,
      failedRequest,
      filters,
      networks,
      openFilter,
      pendingRequest,
      result,
      hasHistoricalAccess,
      hasRealTimeAccess,
      isDownloadingSitesReport: fetching,
      maxHistoricalMonths,
      remainingDownloads: tenant?.remainingDownloads || 0,
    },
  };
};

export default useSiteLookup;
